import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from 'react-toastify';
import { audioService } from './audioService';

const NotificationContext = createContext();
const POLLING_INTERVAL = 300000; // 300 seconds

export function NotificationProvider({ children }) {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [user, setUser] = useState(null);
    const [isPermissionGranted, setIsPermissionGranted] = useState(false);
    const channelRef = useRef(null);
    const pollingRef = useRef(null);

    // Initialize user and permissions
    useEffect(() => {
        const initialize = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            setUser(user);
            setIsPermissionGranted(Notification.permission === 'granted');
            await audioService.init().catch(e => console.log('Audio init:', e));
        };
        initialize();
    }, []);

    // Fetch notifications (using user from state)
    const fetchNotifications = useCallback(async () => {
        if (!user) return;

        const { data, error } = await supabase
            .from('notifications')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false });

        if (!error) {
            setNotifications(data || []);
            setUnreadCount(data?.filter(n => !n.is_read).length || 0);
        }
    }, [user]);

    // Notification handler
    const showBrowserNotification = useCallback(async (notification) => {
        if (!isPermissionGranted) return;

        try {
            const showNotif = () => {
                const notif = new Notification(notification.title, {
                    body: notification.message,
                    icon: '/notification-icon.png',
                    data: { url: notification.url || window.location.href }
                });
                notif.onclick = () => window.focus();
            };

            if ('serviceWorker' in navigator) {
                const registration = await navigator.serviceWorker.ready;
                await registration.showNotification(
                    notification.title || "New Notification",
                    {
                        body: notification.message,
                        icon: '/notification-icon.png'
                    }
                );
            } else {
                showNotif();
            }

            await audioService.play().catch(e => console.log('Sound:', e));
        } catch (error) {
            console.error('Notification failed:', error);
        }
    }, [isPermissionGranted]);

    // WebSocket Realtime Connection
    const setupRealtime = useCallback(() => {
        if (!user || channelRef.current) return;

        const channel = supabase.channel(`user_${user.id}_notifications`)
            .on('postgres_changes', {
                event: 'INSERT',
                schema: 'public',
                table: 'notifications',
                filter: `user_id=eq.${user.id}`
            }, (payload) => {
                setNotifications(prev => [payload.new, ...prev]);
                setUnreadCount(prev => prev + 1);
                showBrowserNotification(payload.new);
            })
            .subscribe((status) => {
                if (status === 'SUBSCRIBED') {
                    console.log('Realtime connected');
                    if (pollingRef.current) {
                        clearInterval(pollingRef.current);
                        pollingRef.current = null;
                    }
                }
            });

        channelRef.current = channel;
        return () => channel.unsubscribe();
    }, [user, showBrowserNotification]);

    // Polling Fallback
    const startPolling = useCallback(() => {
        if (pollingRef.current) return;

        console.log('Starting polling fallback');
        pollingRef.current = setInterval(() => {
            fetchNotifications();
        }, POLLING_INTERVAL);

        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
        };
    }, [fetchNotifications]);

    // Hybrid Connection Manager
    useEffect(() => {
        if (!user) return;

        fetchNotifications();

        // First try WebSocket
        const cleanupRealtime = setupRealtime();

        // Set up polling as fallback
        const fallbackTimer = setTimeout(() => {
            if (!channelRef.current || channelRef.current.state !== 'joined') {
                startPolling();
            }
        }, 5000); // Wait 5 seconds before falling back

        return () => {
            cleanupRealtime?.();
            clearTimeout(fallbackTimer);
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
        };
    }, [user, fetchNotifications, setupRealtime, startPolling]);

    // Test notification
    const sendTestNotification = useCallback(async () => {
        if (!user) return;

        const { error } = await supabase
            .from('notifications')
            .insert({
                user_id: user.id,
                title: 'Test Notification',
                message: 'This is a test notification',
                is_read: false
            });

        if (error) toast.error('Test failed: ' + error.message);
    }, [user]);

    // Mark as read
    const markAsRead = useCallback(async (id) => {
        const { error } = await supabase
            .from('notifications')
            .update({ is_read: true })
            .eq('id', id);

        if (!error) {
            setNotifications(prev => prev.map(n =>
                n.id === id ? { ...n, is_read: true } : n
            ));
            setUnreadCount(prev => prev - 1);
        }
    }, []);

    return (
        <NotificationContext.Provider
            value={{
                notifications,
                unreadCount,
                markAsRead,
                sendTestNotification,
                isPermissionGranted,
                fetchNotifications
            }}
        >
            {children}
        </NotificationContext.Provider>
    );
}

export const useNotifications = () => useContext(NotificationContext);